import {
  assertDesignSystemGenerationRetrievalAdmitted,
  type DesignSystemGenerationCatalogComponent,
  type DesignSystemGenerationRequirement,
  type DesignSystemGenerationRetrievalMatch,
  type DesignSystemGenerationRetrievalRequest,
  type DesignSystemGenerationRetrievalResponse,
  type DesignSystemGenerationReleaseIdentity,
} from '@cogo/headless-core/design-system-generation-retrieval'
import {
  validateSourceDesignSystemGenerationLineage,
  type SourceDesignSystemGenerationLineageReport,
  type SourceDesignSystemGenerationLocatorIndex,
} from '@cogo/headless-core/source-design-system-generation-lineage'
import {
  assertOpenPencilGenerationAdmitted,
  type OpenPencilOntologyFactMapItem,
} from '@cogo/source-openpencil'
import type { OpenPencilSourceWorkspace } from '@/features/openpencil-figma/openpencil-source-workspace'
import {
  resolveProjectDesignSystemGenerationBinding,
  type StudioProjectDesignSystemGenerationBinding,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import type { ProjectDesignSystemRegistrationSummary } from '@/features/design-system/lib/register-project-design-system'

export const STUDIO_PROMOTED_DESIGN_SYSTEM_GENERATION_CONTRACT_SCHEMA_VERSION =
  'cogo.studio-promoted-design-system-generation-contract.v1' as const
export const STUDIO_PROMOTED_SOURCE_OCCURRENCE_AUTHORITY =
  'caller-bounded-final-studio-gate-required' as const

const STUDIO_FLOWBITE_SINGLE_SELECT_GENERATION_RELEASE = {
  packageId: 'cogo.design-data.flowbite-single-select',
  packageVersion: '1.0.0',
  releaseId: 'ds-release-7ea1cc33b535fcd76b52bc0ba8ad8f19071b0d5a',
  packageSnapshotHash:
    'sha256:c1ec75d8b5358d8392c843b756566fc4180be33758f43fd020a6e5804e122a0c',
  identityHash:
    'sha256:8f2bb3aba259cd31c1f34077edc7f51d5bc1bdd80f3c1f8c5b5ead39d7fb8a9a',
  catalogContentHash:
    'sha256:957adb39808435765b94b3d2f237e99ade481ff52604a59d4fad50e1bf775443',
} as const

export interface StudioSourceDesignSystemGenerationContext {
  request: DesignSystemGenerationRetrievalRequest
  retrievalResponse: DesignSystemGenerationRetrievalResponse
  sourceOccurrenceFactIds: string[]
}

export interface StudioPromotedSourceOccurrence {
  sourceNodeRef: string
  sourceFactRefs: string[]
  observedLabels: string[]
}

type AdmittedRetrievalMatch = DesignSystemGenerationRetrievalMatch & {
  decision: 'reuse'
  matchedComponentId: string
  generationAllowed: true
}

export type StudioPromotedDesignSystemGenerationContract = Record<
  string,
  unknown
> & {
  schemaVersion: typeof STUDIO_PROMOTED_DESIGN_SYSTEM_GENERATION_CONTRACT_SCHEMA_VERSION
  authority: 'tool'
  target: 'web'
  sourceOccurrenceAuthority: typeof STUDIO_PROMOTED_SOURCE_OCCURRENCE_AUTHORITY
  sourceAuthority: DesignSystemGenerationRetrievalRequest['sourceAuthority']
  releaseIdentity: DesignSystemGenerationReleaseIdentity
  catalogContentHash: string
  requirements: DesignSystemGenerationRequirement[]
  matches: AdmittedRetrievalMatch[]
  components: DesignSystemGenerationCatalogComponent[]
  sourceOccurrences: StudioPromotedSourceOccurrence[]
  promptText: string
  finalJsonWriter: false
}

interface OpenPencilSourceSnapshot {
  sourceId: string
  fileSha256: string
  sourceRevision: number
  workspaceRevision: number
  factMap: OpenPencilOntologyFactMapItem[]
  surfaceDecisions: OpenPencilSourceWorkspace['surfaceDecisions']
  assemblyManifest: NonNullable<OpenPencilSourceWorkspace['assemblyManifest']>
}

interface TrustedPromotedContractEvidence {
  context: StudioSourceDesignSystemGenerationContext
  sourceSnapshot: OpenPencilSourceSnapshot
}

const trustedPromotedContracts = new WeakMap<
  object,
  TrustedPromotedContractEvidence
>()

const LOCAL_SOURCE_REF = /^[A-Za-z0-9][A-Za-z0-9_.:;#-]*$/
const UNSAFE_LABEL = /[\u0000-\u001F\u007F\u2028\u2029]/u

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function sameIds(left: string[], right: string[]): boolean {
  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()
  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((value, index) => value === sortedRight[index])
  )
}

function assemblyManifestMatchesWorkspace(
  workspace: OpenPencilSourceWorkspace,
  manifest: NonNullable<OpenPencilSourceWorkspace['assemblyManifest']>,
): boolean {
  const acceptedPageIds = Object.values(workspace.pageDecisions)
    .filter((decision) => decision.status === 'accepted')
    .map((decision) => decision.pageId)
  const acceptedSectionIds = Object.values(workspace.sectionDecisions)
    .filter((decision) => decision.status === 'accepted')
    .map((decision) => decision.sectionId)
  const acceptedScreenSurfaceIds = Object.values(
    workspace.surfaceDecisions,
  )
    .filter(
      (decision) =>
        decision.status === 'accepted' && decision.role === 'screen',
    )
    .map((decision) => decision.surfaceId)
  const acceptedTokenIds = Object.values(workspace.tokenDecisions)
    .filter((decision) => decision.status === 'accepted')
    .map((decision) => decision.tokenId)
  const mappedSurfaceIds = manifest.storyMap.flatMap(
    (story) => story.surfaceIds,
  )

  return (
    sameIds(manifest.selectedPageIds, acceptedPageIds) &&
    sameIds(manifest.selectedSectionIds, acceptedSectionIds) &&
    sameIds(manifest.selectedSurfaceIds, acceptedScreenSurfaceIds) &&
    sameIds(manifest.selectedTokenIds, acceptedTokenIds) &&
    sameIds(mappedSurfaceIds, acceptedScreenSurfaceIds) &&
    manifest.visualJudgementIds.every((judgementId) =>
      Boolean(workspace.visualJudgements[judgementId]),
    )
  )
}

export function isStudioPromotedDesignSystemGenerationContract(
  value: unknown,
): value is StudioPromotedDesignSystemGenerationContract {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      STUDIO_PROMOTED_DESIGN_SYSTEM_GENERATION_CONTRACT_SCHEMA_VERSION
  )
}

function sourceRevision(workspace: OpenPencilSourceWorkspace): number {
  return workspace.bundle.revisionLineage?.sourceRevision ?? 1
}

function snapshotActiveSourceWorkspace(
  workspace: OpenPencilSourceWorkspace,
): OpenPencilSourceSnapshot {
  const generationAdmission = workspace.bundle.generationAdmission
  if (
    generationAdmission.status !== 'admitted' ||
    generationAdmission.reasonCodes.length > 0 ||
    generationAdmission.requiredGateIds.length > 0 ||
    generationAdmission.evaluatedAt !== workspace.updatedAt
  ) {
    throw new Error(
      'Studio source/design-system generation requires a current clean OpenPencil generation admission.',
    )
  }
  assertOpenPencilGenerationAdmitted(workspace.bundle)
  const fileSha256 = workspace.bundle.fileSha256
  if (!fileSha256) {
    throw new Error(
      'Studio source/design-system generation requires the active OpenPencil file hash.',
    )
  }
  if (workspace.sourceId !== workspace.bundle.sourceId) {
    throw new Error(
      'Studio source/design-system generation blocked: active workspace sourceId does not match its admitted bundle.',
    )
  }
  const assemblyManifest = workspace.assemblyManifest
  if (
    !Number.isInteger(workspace.revision) ||
    workspace.revision < 0 ||
    !assemblyManifest ||
    assemblyManifest.status !== 'validated' ||
    assemblyManifest.blockers.length > 0 ||
    assemblyManifest.sourceId !== workspace.sourceId ||
    assemblyManifest.sourceRevision !== workspace.revision ||
    assemblyManifest.mayWriteApplicationJson !== false ||
    !assemblyManifestMatchesWorkspace(workspace, assemblyManifest)
  ) {
    throw new Error(
      'Studio source/design-system generation requires a matching validated OpenPencil assemblyManifest.',
    )
  }
  return {
    sourceId: workspace.bundle.sourceId,
    fileSha256,
    sourceRevision: sourceRevision(workspace),
    workspaceRevision: workspace.revision,
    factMap: structuredClone(workspace.bundle.factMap),
    surfaceDecisions: structuredClone(workspace.surfaceDecisions),
    assemblyManifest: structuredClone(assemblyManifest),
  }
}

function factNodeRef(fact: OpenPencilOntologyFactMapItem): string | null {
  const sourceNodeId = fact.evidence.sourceNodeId
  if (typeof sourceNodeId === 'string' && sourceNodeId.trim()) {
    return sourceNodeId.trim()
  }
  const nodeId = fact.evidence.nodeId
  return typeof nodeId === 'string' && nodeId.trim() ? nodeId.trim() : null
}

function buildSourceOccurrences(
  source: OpenPencilSourceSnapshot,
  selectedFactIds: string[],
): StudioPromotedSourceOccurrence[] {
  const uniqueFactIds = [...new Set(selectedFactIds)]
  if (
    uniqueFactIds.length === 0 ||
    uniqueFactIds.length !== selectedFactIds.length ||
    uniqueFactIds.length > 32
  ) {
    throw new Error(
      'Studio source/design-system generation requires 1..32 unique sourceOccurrenceFactIds.',
    )
  }
  const factById = new Map(source.factMap.map((fact) => [fact.id, fact]))
  const selectedSurfaceIds = new Set(
    source.assemblyManifest.selectedSurfaceIds,
  )
  const assembledSurfaceIds = new Set(
    source.assemblyManifest.storyMap.flatMap((story) => story.surfaceIds),
  )
  const occurrences = new Map<string, StudioPromotedSourceOccurrence>()
  for (const factId of uniqueFactIds) {
    const fact = factById.get(factId)
    if (!fact) {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} is not present in the active OpenPencil factMap.`,
      )
    }
    if (fact.kind !== 'surface-candidate') {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} is not a screen-level surface occurrence.`,
      )
    }
    const surfaceDecision = source.surfaceDecisions[factId]
    if (
      !surfaceDecision ||
      surfaceDecision.surfaceId !== factId ||
      surfaceDecision.status !== 'accepted' ||
      surfaceDecision.role !== 'screen' ||
      surfaceDecision.actor !== 'human'
    ) {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} is not a human-accepted screen surface.`,
      )
    }
    if (
      !selectedSurfaceIds.has(factId) ||
      !assembledSurfaceIds.has(factId)
    ) {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} is not selected by the validated assemblyManifest.`,
      )
    }
    const sourceNodeRef = factNodeRef(fact)
    if (
      !sourceNodeRef ||
      sourceNodeRef.length > 256 ||
      !LOCAL_SOURCE_REF.test(sourceNodeRef) ||
      fact.id.length > 256 ||
      !LOCAL_SOURCE_REF.test(fact.id)
    ) {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} has no bounded source-authority-local node reference.`,
      )
    }
    const observedLabel = fact.label.trim()
    if (
      !observedLabel ||
      observedLabel.length > 256 ||
      UNSAFE_LABEL.test(observedLabel)
    ) {
      throw new Error(
        `Studio source/design-system generation blocked: source fact ${factId} has no bounded screen-level label.`,
      )
    }
    const occurrence = occurrences.get(sourceNodeRef) ?? {
      sourceNodeRef,
      sourceFactRefs: [],
      observedLabels: [],
    }
    occurrence.sourceFactRefs.push(fact.id)
    if (!occurrence.observedLabels.includes(observedLabel)) {
      occurrence.observedLabels.push(observedLabel)
    }
    if (
      occurrence.sourceFactRefs.length > 16 ||
      occurrence.observedLabels.length > 8
    ) {
      throw new Error(
        `Studio source/design-system generation blocked: source occurrence ${sourceNodeRef} exceeds the bounded platform contract.`,
      )
    }
    occurrences.set(sourceNodeRef, occurrence)
  }
  const result = [...occurrences.values()]
  if (result.length !== 1) {
    throw new Error(
      'Studio source/design-system generation requires exactly one distinct source occurrence.',
    )
  }
  return result
}

function assertHostPinnedFlowbiteSingleSelectRelease(
  context: StudioSourceDesignSystemGenerationContext,
): void {
  const selectedRelease = context.request.selectedRelease
  const responseRelease = context.retrievalResponse.releaseIdentity
  const catalog = context.retrievalResponse.catalog
  const catalogRelease = catalog.releaseIdentity
  const pin = STUDIO_FLOWBITE_SINGLE_SELECT_GENERATION_RELEASE
  const releaseMatches = (
    release: DesignSystemGenerationReleaseIdentity,
  ) =>
    release.packageId === pin.packageId &&
    release.packageVersion === pin.packageVersion &&
    release.releaseId === pin.releaseId &&
    release.packageSnapshotHash === pin.packageSnapshotHash &&
    release.identityHash === pin.identityHash &&
    release.immutable === true

  if (
    selectedRelease?.packageId !== pin.packageId ||
    selectedRelease.releaseId !== pin.releaseId ||
    !releaseMatches(responseRelease) ||
    catalog.packageId !== pin.packageId ||
    catalog.packageVersion !== pin.packageVersion ||
    !releaseMatches(catalogRelease) ||
    catalog.contentHash !== pin.catalogContentHash
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: retrieval does not match the host-pinned Flowbite single-select release and catalog content hash.',
    )
  }
}

function sameSourceAuthority(
  request: DesignSystemGenerationRetrievalRequest,
  source: OpenPencilSourceSnapshot,
): boolean {
  return (
    request.sourceAuthority.sourceId === source.sourceId &&
    request.sourceAuthority.fileSha256 === source.fileSha256 &&
    request.sourceAuthority.sourceRevision === source.sourceRevision
  )
}

function buildFromSnapshot(
  context: StudioSourceDesignSystemGenerationContext,
  sourceSnapshot: OpenPencilSourceSnapshot,
): StudioPromotedDesignSystemGenerationContract {
  if (!sameSourceAuthority(context.request, sourceSnapshot)) {
    throw new Error(
      'Studio source/design-system generation blocked: retrieval request source authority is stale for the active OpenPencil workspace.',
    )
  }
  assertHostPinnedFlowbiteSingleSelectRelease(context)
  const admission = assertDesignSystemGenerationRetrievalAdmitted({
    request: context.request,
    response: context.retrievalResponse,
    expectedCatalogContentHash:
      STUDIO_FLOWBITE_SINGLE_SELECT_GENERATION_RELEASE.catalogContentHash,
  })
  if (context.request.target !== 'web') {
    throw new Error(
      'Studio source/design-system generation currently admits only the Web target.',
    )
  }
  const matches = context.retrievalResponse.matches.filter(
    (match): match is AdmittedRetrievalMatch =>
      match.decision === 'reuse' &&
      typeof match.matchedComponentId === 'string' &&
      match.generationAllowed === true,
  )
  if (
    matches.length !== context.retrievalResponse.matches.length ||
    matches.length !== admission.matchedRequirementIds.length
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: every admitted requirement must have one exact reuse match.',
    )
  }
  const sourceOccurrences = buildSourceOccurrences(
    sourceSnapshot,
    context.sourceOccurrenceFactIds,
  )
  const contract: StudioPromotedDesignSystemGenerationContract = {
    schemaVersion:
      STUDIO_PROMOTED_DESIGN_SYSTEM_GENERATION_CONTRACT_SCHEMA_VERSION,
    authority: 'tool',
    target: 'web',
    sourceOccurrenceAuthority:
      STUDIO_PROMOTED_SOURCE_OCCURRENCE_AUTHORITY,
    sourceAuthority: structuredClone(context.request.sourceAuthority),
    releaseIdentity: structuredClone(
      context.retrievalResponse.releaseIdentity,
    ),
    catalogContentHash:
      STUDIO_FLOWBITE_SINGLE_SELECT_GENERATION_RELEASE.catalogContentHash,
    requirements: structuredClone(context.request.requirements),
    matches: structuredClone(matches),
    components: structuredClone(context.retrievalResponse.catalog.components),
    sourceOccurrences,
    promptText: context.retrievalResponse.catalog.promptText,
    finalJsonWriter: false,
  }
  const evidence: TrustedPromotedContractEvidence = {
    context: structuredClone(context),
    sourceSnapshot: structuredClone(sourceSnapshot),
  }
  trustedPromotedContracts.set(contract, evidence)
  return contract
}

export function buildStudioPromotedDesignSystemGenerationContract(input: {
  workspace: OpenPencilSourceWorkspace
  context: StudioSourceDesignSystemGenerationContext
}): StudioPromotedDesignSystemGenerationContract {
  return buildFromSnapshot(
    input.context,
    snapshotActiveSourceWorkspace(input.workspace),
  )
}

// ── Hard-refresh resume trust re-admission ──────────────────────────────────
// The trust WeakMap above is keyed by object identity, which is the correct
// anti-model-authoring guard for the live path but is lost on a hard refresh:
// a resumed job rehydrates its appSpec (and the promoted contract inside it)
// from JSON persistence, so the resolver would fail-close on a legitimately
// host-admitted contract. Re-admission is host-stamp based: at generation
// time the host persists a canonical-JSON sha256 of the trusted contract plus
// the host-held retrieval context; on resume it verifies the rehydrated
// contract against that stamp and REBUILDS the contract through every
// admission gate against the live workspace, requiring the rebuild to
// reproduce the stamped hash byte-exactly.

const STUDIO_PROMOTED_CONTRACT_RESUME_TRUST_SCHEMA_VERSION =
  'cogo.studio-promoted-contract-resume-trust.v1' as const

function resumeTrustStampStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

function resumeTrustStampKey(projectId: string): string {
  return `cogo-studio:promoted-contract-resume-trust:${projectId}`
}

function canonicalTrustJson(value: unknown): string {
  if (value === null) return 'null'
  if (typeof value === 'string') return JSON.stringify(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') {
    return Number.isFinite(value) ? JSON.stringify(value) : 'null'
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalTrustJson(item)).join(',')}]`
  }
  if (isRecord(value)) {
    const entries = Object.entries(value)
      .filter(([, item]) => item !== undefined && typeof item !== 'function')
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    return `{${entries
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalTrustJson(item)}`)
      .join(',')}}`
  }
  return JSON.stringify(String(value))
}

export async function computeStudioPromotedDesignSystemGenerationContractSha256(
  contract: StudioPromotedDesignSystemGenerationContract,
): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonicalTrustJson(contract)),
  )
  const hex = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
  return `sha256:${hex}`
}

/**
 * Persist the host-authored resume trust stamp for an already-trusted
 * promoted contract. Only host code can call this with a contract that is in
 * the trust WeakMap, and only host code writes the app-generation job store —
 * a model-fabricated appSpec therefore cannot cause a matching stamp to exist
 * at persist time, which is the invariant that keeps re-admission from
 * becoming a model-authoring channel. Returns false when durable storage is
 * unavailable (resume itself is impossible then, so generation proceeds and
 * a later resume fails closed).
 */
export async function stampStudioPromotedDesignSystemGenerationContractForResume(input: {
  projectId: string
  contract: StudioPromotedDesignSystemGenerationContract
}): Promise<boolean> {
  const evidence = trustedPromotedContracts.get(input.contract)
  if (!evidence) {
    throw new Error(
      'Studio cannot stamp resume trust for an untrusted promoted design-system contract.',
    )
  }
  const storage = resumeTrustStampStorage()
  if (!storage) return false
  try {
    storage.setItem(
      resumeTrustStampKey(input.projectId),
      JSON.stringify({
        schemaVersion: STUDIO_PROMOTED_CONTRACT_RESUME_TRUST_SCHEMA_VERSION,
        projectId: input.projectId,
        contractSha256:
          await computeStudioPromotedDesignSystemGenerationContractSha256(
            input.contract,
          ),
        context: evidence.context,
      }),
    )
    return true
  } catch {
    return false
  }
}

/**
 * Fail-closed re-admission of a JSON-rehydrated promoted contract on a
 * hard-refresh resume. The rehydrated object must hash to the host-stamped
 * value, and the contract must be REBUILDABLE through every admission gate
 * from the stamped host-held retrieval context against the CURRENT
 * workspace, reproducing the same hash. The returned contract is the rebuilt,
 * trust-registered object — never the rehydrated input.
 */
export async function readmitStudioPromotedDesignSystemGenerationContractAfterReload(input: {
  projectId: string
  contract: unknown
  workspace: OpenPencilSourceWorkspace | null | undefined
}): Promise<StudioPromotedDesignSystemGenerationContract> {
  if (!isStudioPromotedDesignSystemGenerationContract(input.contract)) {
    throw new Error(
      'Studio rejected an untrusted promoted design-system contract at the app-generation tool boundary.',
    )
  }
  const storage = resumeTrustStampStorage()
  const raw = storage?.getItem(resumeTrustStampKey(input.projectId)) ?? null
  let stamp: Record<string, unknown> | null = null
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      stamp = isRecord(parsed) ? parsed : null
    } catch {
      stamp = null
    }
  }
  const stampContext = stamp && isRecord(stamp.context) ? stamp.context : null
  if (
    !stamp ||
    stamp.schemaVersion !==
      STUDIO_PROMOTED_CONTRACT_RESUME_TRUST_SCHEMA_VERSION ||
    stamp.projectId !== input.projectId ||
    typeof stamp.contractSha256 !== 'string' ||
    !/^sha256:[a-f0-9]{64}$/.test(stamp.contractSha256) ||
    !stampContext ||
    !isRecord(stampContext.request) ||
    !isRecord(stampContext.retrievalResponse) ||
    !Array.isArray(stampContext.sourceOccurrenceFactIds)
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: the resumed promoted contract has no host resume trust stamp.',
    )
  }
  const rehydratedSha =
    await computeStudioPromotedDesignSystemGenerationContractSha256(
      input.contract,
    )
  if (rehydratedSha !== stamp.contractSha256) {
    throw new Error(
      'Studio source/design-system generation blocked: the resumed promoted contract does not match the host resume trust stamp.',
    )
  }
  if (!input.workspace) {
    throw new Error(
      'Studio source/design-system generation requires a non-null current OpenPencil workspace.',
    )
  }
  const rebuilt = buildStudioPromotedDesignSystemGenerationContract({
    workspace: input.workspace,
    context: stampContext as unknown as StudioSourceDesignSystemGenerationContext,
  })
  const rebuiltSha =
    await computeStudioPromotedDesignSystemGenerationContractSha256(rebuilt)
  if (rebuiltSha !== stamp.contractSha256) {
    throw new Error(
      'Studio source/design-system generation blocked: the current workspace no longer reproduces the host-stamped promoted contract.',
    )
  }
  return rebuilt
}

// ── Source-bound generation is NOT implemented ─────────────────────────────
//
// STATUS (measured 2026-07-30): "import a .fig → derive a design system →
// generate the app" does not exist as a product path. What exists is this
// bridge, which can promote ONE pre-pinned proof artifact:
//
//   • `assertHostPinnedFlowbiteSingleSelectRelease` (this file) hard-pins the
//     packageId/releaseId/snapshot/identity/catalog hashes of a single
//     Flowbite single-select release. No other design system can be admitted.
//   • `buildSourceOccurrences` requires EXACTLY ONE distinct source
//     occurrence, so a real multi-surface .fig cannot be admitted either.
//   • `target` must be `web`.
//   • Nothing in production ever constructs a
//     `StudioSourceDesignSystemGenerationContext`. The retrieval service
//     exists on the platform (`POST /kg/find-design-system-assets`,
//     cogo-kg-worker) and a request builder exists
//     (`buildOpenPencilDesignSystemGenerationRetrievalRequest`,
//     @cogo/headless-core), but Studio never calls either for generation —
//     the only callers are unit tests and the offline
//     `scripts/agentic/wonbanking-fig-openpencil-e2e-gate.ts` gate.
//
// So this is a not-implemented feature, not a loose wire: wiring the host
// call alone would still fail on the release pin and the single-occurrence
// rule for any real user source. Until the lane is built, the guard below
// must fail CLOSED (never generate while silently ignoring the user's
// imported source) and must say so in words an end user can act on.
//
// The user-facing consequence today: a project with an imported source can
// never generate an app, and there is no in-product control that removes an
// imported source from a project (`clearOpenPencilFigmaSource` has no UI
// caller) — only opening/creating a different project clears it. The message
// therefore points at the action that actually works.
export const STUDIO_SOURCE_BOUND_GENERATION_UNAVAILABLE_REASON_CODE =
  'studio-source-bound-generation-unavailable' as const

export const STUDIO_SOURCE_BOUND_GENERATION_UNAVAILABLE_MESSAGE =
  'Generating an app from an imported design source is not available yet. ' +
  'This project has an imported design source (.fig), and Studio will not ' +
  'generate an app that silently ignores it. To generate now, start a new ' +
  'project without importing a source; this project keeps its imported ' +
  'source for review.'

export class StudioSourceBoundGenerationUnavailableError extends Error {
  readonly reasonCode =
    STUDIO_SOURCE_BOUND_GENERATION_UNAVAILABLE_REASON_CODE

  constructor(message: string = STUDIO_SOURCE_BOUND_GENERATION_UNAVAILABLE_MESSAGE) {
    super(message)
    this.name = 'StudioSourceBoundGenerationUnavailableError'
  }
}

/**
 * Independently re-derive whether the active source is actually FOLLOWED by a
 * registered project design system (R1).
 *
 * Deliberately not a boolean parameter. The backstops below exist to stop a
 * generation that silently ignores an imported source, and a caller-supplied
 * "trust me, it's followed" flag would be exactly the bypass they guard
 * against. Both call sites hand over the same raw evidence the early gate used
 * — the persisted registration and the live workspace — and this recomputes the
 * verdict, so an agent reaching the MCP tool boundary directly is held to the
 * identical drift and rights-ceiling checks.
 */
function followedProjectDesignSystemBinding(input: {
  workspace: OpenPencilSourceWorkspace | null | undefined
  projectDesignSystemRegistration?:
    | ProjectDesignSystemRegistrationSummary
    | null
  sourceProjectId?: string | null
}): StudioProjectDesignSystemGenerationBinding | null {
  if (!input.workspace || !input.projectDesignSystemRegistration) return null
  try {
    return resolveProjectDesignSystemGenerationBinding({
      registration: input.projectDesignSystemRegistration,
      workspace: input.workspace,
      sourceProjectId: input.sourceProjectId,
    })
  } catch {
    // Drift, a tampered ceiling, or a missing render floor. Returning null
    // routes into the ordinary fail-closed refusal below rather than throwing
    // a second, less actionable message over the top of the early gate's.
    return null
  }
}

export function withStudioSourceDesignSystemGenerationContract<
  T extends { designSystemContract?: Record<string, unknown> },
>(input: {
  appSpec: T
  workspace: OpenPencilSourceWorkspace | null | undefined
  context?: StudioSourceDesignSystemGenerationContext
  /** R1 evidence — the persisted `settings.projectDesignSystem` record. */
  projectDesignSystemRegistration?:
    | ProjectDesignSystemRegistrationSummary
    | null
  sourceProjectId?: string | null
}): T {
  if (!input.context) {
    if (isStudioPromotedDesignSystemGenerationContract(input.appSpec.designSystemContract)) {
      throw new Error(
        'Studio rejected a model-supplied promoted design-system contract. Only the host source/retrieval bridge may create it.',
      )
    }
    if (input.workspace && !followedProjectDesignSystemBinding(input)) {
      // Last-resort backstop. `assertStudioSourceBoundGenerationSupported`
      // (start-app-generation-from-prompt.ts) fires this same failure before
      // planning starts, so reaching here means a caller bypassed the early
      // gate — still fail closed, still with the user-legible wording.
      throw new StudioSourceBoundGenerationUnavailableError()
    }
    return input.appSpec
  }
  if (!input.workspace) {
    throw new Error(
      'Studio source/design-system generation requires an active admitted OpenPencil workspace.',
    )
  }
  return {
    ...input.appSpec,
    designSystemContract: buildStudioPromotedDesignSystemGenerationContract({
      workspace: input.workspace,
      context: input.context,
    }),
  }
}

export function resolveStudioDesignSystemContractAtToolBoundary(input: {
  appSpecContract: unknown
  payloadContract: unknown
  activeWorkspace: OpenPencilSourceWorkspace | null | undefined
  /** R1 evidence — the persisted `settings.projectDesignSystem` record. */
  projectDesignSystemRegistration?:
    | ProjectDesignSystemRegistrationSummary
    | null
  sourceProjectId?: string | null
}): Record<string, unknown> | undefined {
  if (isStudioPromotedDesignSystemGenerationContract(input.payloadContract)) {
    throw new Error(
      'Studio rejected a model-supplied promoted design-system contract at the app-generation tool boundary.',
    )
  }
  if (isStudioPromotedDesignSystemGenerationContract(input.appSpecContract)) {
    const evidence = trustedPromotedContracts.get(input.appSpecContract)
    if (!evidence) {
      throw new Error(
        'Studio rejected an untrusted promoted design-system contract at the app-generation tool boundary.',
      )
    }
    const sourceSnapshot = matchingCurrentSourceSnapshot(
      input.activeWorkspace,
      evidence,
    )
    const rebuilt = buildFromSnapshot(evidence.context, sourceSnapshot)
    if (
      JSON.stringify(rebuilt.sourceOccurrences) !==
      JSON.stringify(
        buildSourceOccurrences(
          evidence.sourceSnapshot,
          evidence.context.sourceOccurrenceFactIds,
        ),
      )
    ) {
      throw new Error(
        'Studio source/design-system generation blocked: active OpenPencil source occurrences changed after retrieval admission.',
      )
    }
    return rebuilt
  }
  if (
    input.activeWorkspace &&
    !followedProjectDesignSystemBinding({
      workspace: input.activeWorkspace,
      projectDesignSystemRegistration: input.projectDesignSystemRegistration,
      sourceProjectId: input.sourceProjectId,
    })
  ) {
    // Reached through the MCP tool boundary instead of the chat service (e.g.
    // an agent calling `cogo.studio.requestAppGeneration` directly while a
    // source is imported). The imported source is not followed by any admitted
    // design authority here, so surface the same typed, user-legible failure
    // rather than a contract string. The untrusted/model-supplied branches
    // above keep their specific wording — those are tamper signals, not an
    // unimplemented feature.
    //
    // R1 — a project that DID register its .fig design system passes: the
    // generation is following the source, which is the only thing this
    // refusal ever protected. It carries no promoted contract, so the
    // fall-through below returns the ordinary preset-shaped contract.
    throw new StudioSourceBoundGenerationUnavailableError()
  }
  if (isRecord(input.payloadContract)) return input.payloadContract
  return isRecord(input.appSpecContract) ? input.appSpecContract : undefined
}

function matchingCurrentSourceSnapshot(
  activeWorkspace: OpenPencilSourceWorkspace | null | undefined,
  evidence: TrustedPromotedContractEvidence,
): OpenPencilSourceSnapshot {
  if (!activeWorkspace) {
    throw new Error(
      'Studio source/design-system generation requires a non-null current OpenPencil workspace.',
    )
  }
  const current = snapshotActiveSourceWorkspace(activeWorkspace)
  const expected = evidence.sourceSnapshot
  if (
    current.sourceId !== expected.sourceId ||
    current.fileSha256 !== expected.fileSha256 ||
    current.sourceRevision !== expected.sourceRevision ||
    current.workspaceRevision !== expected.workspaceRevision ||
    current.assemblyManifest.manifestId !==
      expected.assemblyManifest.manifestId
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: the current OpenPencil workspace no longer matches the admitted source workspace.',
    )
  }
  const currentOccurrences = buildSourceOccurrences(
    current,
    evidence.context.sourceOccurrenceFactIds,
  )
  const admittedOccurrences = buildSourceOccurrences(
    expected,
    evidence.context.sourceOccurrenceFactIds,
  )
  if (
    JSON.stringify(currentOccurrences) !==
    JSON.stringify(admittedOccurrences)
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: active OpenPencil source occurrences changed after retrieval admission.',
    )
  }
  return current
}

function trustedEvidenceForContract(
  contract: unknown,
): TrustedPromotedContractEvidence | null {
  if (!isStudioPromotedDesignSystemGenerationContract(contract)) return null
  const evidence = trustedPromotedContracts.get(contract)
  if (!evidence) {
    throw new Error(
      'Studio cannot validate source/design-system lineage for an untrusted promoted contract.',
    )
  }
  return evidence
}

export function validateStudioGeneratedApplicationSourceDesignSystemLineage(
  application: unknown,
  contract: unknown,
  activeWorkspace: OpenPencilSourceWorkspace | null | undefined,
): SourceDesignSystemGenerationLineageReport | null {
  const evidence = trustedEvidenceForContract(contract)
  if (!evidence) return null
  const currentSourceSnapshot = matchingCurrentSourceSnapshot(
    activeWorkspace,
    evidence,
  )
  assertHostPinnedFlowbiteSingleSelectRelease(evidence.context)
  const sourceOccurrences = buildSourceOccurrences(
    currentSourceSnapshot,
    evidence.context.sourceOccurrenceFactIds,
  )
  const sourceLocatorIndex: SourceDesignSystemGenerationLocatorIndex = {
    sourceOccurrences: sourceOccurrences.map((occurrence) => ({
      sourceNodeRef: occurrence.sourceNodeRef,
      sourceFactRefs: occurrence.sourceFactRefs,
    })),
  }
  const report = validateSourceDesignSystemGenerationLineage({
    application,
    request: evidence.context.request,
    retrievalResponse: evidence.context.retrievalResponse,
    expectedCatalogContentHash:
      STUDIO_FLOWBITE_SINGLE_SELECT_GENERATION_RELEASE.catalogContentHash,
    sourceLocatorIndex,
  })
  if (!report.generationAllowed) {
    throw new Error(
      `Studio source/design-system generation lineage blocked: ${report.blockers
        .map((blocker) => blocker.code)
        .join(', ')}`,
    )
  }
  return report
}

export function hasTrustedStudioSourceDesignSystemGenerationContract(
  contract: unknown,
): boolean {
  return (
    isStudioPromotedDesignSystemGenerationContract(contract) &&
    trustedPromotedContracts.has(contract)
  )
}
